import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '@/services/api';
import { getResolvedMenu, flattenProducts, defaultVariant, getQuote } from '@/services/catalog';
import { useCart } from '@/lib/ordering/useCart';
import * as engine from '@/lib/ordering/cartEngine';
import ProductCard from '@/components/catalog/ProductCard';
import ProductCustomizer from '@/components/catalog/ProductCustomizer';
import PaymentDialog from '@/components/POS/PaymentDialog';
import { hasCapability } from '@/lib/capabilities';
import { formatINR } from '@/lib/money';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, Search, ShoppingCart, Trash2, Plus, Minus, Utensils, Package, Bike, Pause, X, PlayCircle } from 'lucide-react';

const ORDER_TYPES = [
  { id: 'DINE_IN', label: 'Dine-In', icon: Utensils },
  { id: 'TAKEAWAY', label: 'Takeaway', icon: Package },
  { id: 'DELIVERY', label: 'Delivery', icon: Bike },
];

export default function AssistedOrder() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const storeId = user?.store_id;
  const canOrder = hasCapability(user, 'orders.operate') || hasCapability(user, 'menu.manage');

  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('All');
  const [search, setSearch] = useState('');
  const [customizing, setCustomizing] = useState(null);
  const [orderType, setOrderType] = useState('TAKEAWAY');
  const [mobileView, setMobileView] = useState('menu'); // 'menu' | 'cart' — phone screens only
  const [customer, setCustomer] = useState(() => location.state?.prefillCustomer || { name: '', phone: '' });
  const [customerLookup, setCustomerLookup] = useState(null); // null | 'searching' | { found: bool, name?: string, address?: string }
  const [isPostPay, setIsPostPay] = useState(false);
  const [tentCardNumber, setTentCardNumber] = useState('');
  const [kitchenNotes, setKitchenNotes] = useState('');
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomerAddress, setNewCustomerAddress] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [paidOrder, setPaidOrder] = useState(null);
  const [showPayment, setShowPayment] = useState(false);

  // Hold Order — lets staff park an in-progress draft (cart + customer +
  // order type) to serve another customer, then come back and resume it
  // later. Stored client-side in localStorage, same pattern as the proven
  // legacy implementation — this is a pre-checkout draft, never touches
  // the backend until the staff member actually places it.
  const [heldOrders, setHeldOrders] = useState(() => {
    try {
      const saved = localStorage.getItem('sspice_pos_held_orders');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showHeldOrders, setShowHeldOrders] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('sspice_pos_held_orders', JSON.stringify(heldOrders));
    } catch {
      /* ignore quota errors */
    }
  }, [heldOrders]);

  const handleHoldOrder = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty — nothing to hold');
      return;
    }
    const held = {
      id: `hold-${Date.now()}`,
      heldAt: new Date().toISOString(),
      cart,
      customer,
      orderType,
      isPostPay,
      tentCardNumber,
      kitchenNotes,
    };
    setHeldOrders((prev) => [...prev, held]);
    clear();
    setCustomer({ name: '', phone: '' });
    setCustomerLookup(null);
    setTentCardNumber('');
    setKitchenNotes('');
    toast.success('Order held — you can resume it anytime from "Held"');
  };

  const handleResumeHeldOrder = (held) => {
    replaceCart(held.cart);
    setCustomer(held.customer);
    setOrderType(held.orderType);
    setIsPostPay(held.isPostPay);
    setTentCardNumber(held.tentCardNumber || '');
    setKitchenNotes(held.kitchenNotes || '');
    setHeldOrders((prev) => prev.filter((o) => o.id !== held.id));
    setShowHeldOrders(false);
    toast.success('Held order resumed');
  };

  const handleDeleteHeldOrder = (heldId) => {
    setHeldOrders((prev) => prev.filter((o) => o.id !== heldId));
  };

  const { cart, add, setQty, clear, replaceCart } = useCart('sspice_pos_cart');
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!storeId || cart.length === 0) { setQuote(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const items = cart.map((c) => ({ menu_item_id: c.menu_item_id, qty: c.qty, variant: c.variant, modifiers: c.modifiers }));
        const res = await getQuote(storeId, { order_type: orderType, channel: 'pos_walkin', items });
        if (!cancelled) setQuote(res.data);
      } catch { if (!cancelled) setQuote(null); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [cart, orderType]);

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    if (!canOrder) { toast.error('No permission to take orders'); navigate('/pos-dashboard'); return; }
    if (storeId) load();
    // eslint-disable-next-line
  }, []);

  // Auto-lookup the moment a complete 10-digit phone number is entered —
  // no separate search button needed. Shows whether this is an existing
  // customer (auto-filling their name) or genuinely new, without ever
  // requiring staff to type a name if the customer isn't comfortable sharing.
  useEffect(() => {
    const digits = (customer.phone || '').replace(/\D/g, '');
    setShowAddCustomer(false);
    if (digits.length !== 10) {
      setCustomerLookup(null);
      return;
    }
    let cancelled = false;
    setCustomerLookup('searching');
    apiClient.get(`/customers/lookup`, { params: { phone: digits } })
      .then((res) => {
        if (cancelled) return;
        if (res.data.found) {
          const savedAddr = (res.data.customer.addresses || []).find((a) => a.is_default) || (res.data.customer.addresses || [])[0];
          setCustomerLookup({
            found: true,
            name: res.data.customer.name,
            address: savedAddr ? savedAddr.address_line : (res.data.customer.address || null),
          });
          if (!customer.name && res.data.customer.name) {
            setCustomer((prev) => ({ ...prev, name: res.data.customer.name }));
          }
        } else {
          setCustomerLookup({ found: false });
        }
      })
      .catch(() => { if (!cancelled) setCustomerLookup(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [customer.phone]);

  const load = async () => {
    try {
      const res = await getResolvedMenu(storeId, 'pos_walkin');
      setMenu(res.data);
    } catch { toast.error('Failed to load catalog'); }
    finally { setLoading(false); }
  };

  const products = useMemo(() => (menu ? flattenProducts(menu) : []), [menu]);
  const categories = useMemo(() => ['All', ...((menu?.categories || []).map((c) => c.name))], [menu]);
  const filtered = products.filter((p) => (activeCat === 'All' || p.category_name === activeCat) && (!search || p.name.toLowerCase().includes(search.toLowerCase())));

  const productQty = (p) => cart.filter((c) => c.menu_item_id === p.id).reduce((s, c) => s + c.qty, 0);
  const buildSimple = (p) => engine.createCartItem(
    { id: p.id, name: p.name, price: p.effective_price ?? defaultVariant(p)?.price ?? 0, image_url: p.image_url },
    { qty: 1, variant: defaultVariant(p) ? { id: defaultVariant(p).id, name: defaultVariant(p).name, price: defaultVariant(p).price } : null, modifiers: [] }
  );
  const quickAdd = (p) => add(
    { id: p.id, name: p.name, price: p.effective_price ?? defaultVariant(p)?.price ?? 0, image_url: p.image_url },
    { qty: 1, variant: defaultVariant(p) ? { id: defaultVariant(p).id, name: defaultVariant(p).name, price: defaultVariant(p).price } : null, modifiers: [] }
  );
  const inc = (p) => { const it = buildSimple(p); const ex = cart.find((c) => c.signature === it.signature); setQty(it.signature, (ex?.qty || 0) + 1); };
  const dec = (p) => { const it = buildSimple(p); const ex = cart.find((c) => c.signature === it.signature); if (ex) setQty(it.signature, ex.qty - 1); };
  const confirmCustomize = (opts) => { const p = customizing; add({ id: p.id, name: p.name, price: p.effective_price ?? 0, image_url: p.image_url }, opts); setCustomizing(null); };

  const totals = engine.computeTotals(cart);

  const handleAddCustomer = async () => {
    const digits = (customer.phone || '').replace(/\D/g, '');
    if (digits.length !== 10) { toast.error('Enter a valid 10-digit number first'); return; }
    setAddingCustomer(true);
    try {
      await apiClient.post('/customers', { phone: digits, name: customer.name || '', address: newCustomerAddress || '' });
      toast.success('Customer added');
      setCustomerLookup({ found: true, name: customer.name, address: newCustomerAddress || null });
      setShowAddCustomer(false);
    } catch {
      toast.error('Failed to add customer');
    } finally {
      setAddingCustomer(false);
    }
  };

  const placeOrder = async () => {
    // A pending order already exists for this cart (payment was cancelled
    // earlier without clearing) — reopen payment for it instead of
    // creating a duplicate order for the same items.
    if (paidOrder) {
      setShowPayment(true);
      return;
    }
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    setPlacing(true);
    try {
      const body = {
        store_id: storeId,
        order_type: orderType,
        source: 'pos',
        discount: 0,
        payment_method: 'CASH',
        special_instructions: kitchenNotes,
        is_post_pay: isPostPay,
        tent_card_number: tentCardNumber || null,
        customer: (customer.phone || customer.name) ? { name: customer.name, phone: customer.phone } : null,
        items: cart.map((c) => ({
          menu_item_id: c.menu_item_id,
          menu_item_name: c.menu_item_name,
          qty: c.qty,
          price: engine.unitPrice(c),
          notes: c.notes || '',
          variant: c.variant,
          modifiers: c.modifiers,
        })),
      };
      const res = await apiClient.post('/orders', body);
      toast.success(`Order #${res.data.order_number} placed · ₹${res.data.total}`);

      if (isPostPay) {
        // Post Pay orders go straight to the kitchen regardless of
        // payment status — cart clears immediately, same as before.
        clear();
        setCustomer({ name: '', phone: '' });
        setTentCardNumber('');
        setKitchenNotes('');
        setIsPostPay(false);
      } else {
        // Prepaid orders: keep the cart exactly as-is until payment is
        // actually confirmed. The cashier can retry payment, switch
        // method, or explicitly hit "Clear" to abandon it — nothing here
        // silently empties the cart on their behalf.
        setPaidOrder(res.data);
        setShowPayment(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to place order');
    } finally { setPlacing(false); }
  };

  if (!storeId) return <div className="p-8 text-center">No store assigned.</div>;
  if (!canOrder) return <div className="p-8 text-center">Access denied.</div>;

  return (
    <div className="h-screen bg-slate-50 flex flex-col overflow-hidden" data-testid="assisted-order-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pos-dashboard')} data-testid="ao-back"><ChevronLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="font-heading text-lg font-bold text-slate-900">Assisted Ordering</h1>
            <p className="text-xs text-slate-500">{user?.name} · unified catalog · POS channel</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleHoldOrder} data-testid="ao-hold-btn">
            <Pause className="w-4 h-4 mr-1.5" />
            Hold
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setShowHeldOrders(true)}
            className="relative"
            data-testid="ao-held-list-btn"
          >
            <PlayCircle className="w-4 h-4 mr-1.5" />
            Held
            {heldOrders.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {heldOrders.length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu/Cart switcher — phone screens only. On desktop both
          panels already show side-by-side, so this stays hidden there. */}
      <div className="lg:hidden flex border-b border-slate-200 bg-white sticky top-[57px] z-20">
        <button
          data-testid="ao-mobile-tab-menu"
          onClick={() => setMobileView('menu')}
          className={`flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors ${mobileView === 'menu' ? 'border-sspice text-sspice' : 'border-transparent text-slate-400'}`}
        >
          Menu
        </button>
        <button
          data-testid="ao-mobile-tab-cart"
          onClick={() => setMobileView('cart')}
          className={`flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors relative ${mobileView === 'cart' ? 'border-sspice text-sspice' : 'border-transparent text-slate-400'}`}
        >
          Cart
          {totals.count > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-sspice text-white text-[10px] font-bold">
              {totals.count}
            </span>
          )}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row flex-1 max-w-7xl mx-auto w-full min-h-0">
        {/* Catalog */}
        <div className={`flex-1 p-4 overflow-y-auto ${mobileView === 'cart' ? 'hidden lg:block' : 'block'}`}>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search catalog…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ao-search" />
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
            {categories.map((c) => (
              <button key={c} data-testid={`ao-cat-${c}`} onClick={() => setActiveCat(c)} className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold ${activeCat === c ? 'bg-sspice text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>{c}</button>
            ))}
          </div>
          {loading ? (
            <div className="text-center py-16 text-slate-400">Loading catalog…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-400" data-testid="ao-empty">No products. Publish the menu in Menu Management.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} qty={productQty(p)} layout="grid" onAdd={quickAdd} onCustomize={setCustomizing} onInc={inc} onDec={dec} />
              ))}
            </div>
          )}
        </div>

        {/* Cart panel */}
        <aside className={`lg:w-96 flex-shrink-0 bg-white lg:border-l border-slate-200 p-4 flex-col overflow-hidden ${mobileView === 'cart' ? 'flex' : 'hidden lg:flex'}`} data-testid="ao-cart">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-slate-900 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Order ({totals.count})</h2>
            {cart.length > 0 && (
              <button
                onClick={() => {
                  if (paidOrder) {
                    toast.info(`Order #${paidOrder.order_number} left unpaid — find it in Payments & Refunds if you need it.`, { duration: 6000 });
                    setPaidOrder(null);
                  }
                  clear();
                }}
                className="text-xs text-red-500 flex items-center gap-1"
                data-testid="ao-clear"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>

          {/* Order type */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {ORDER_TYPES.map(({ id, label, icon: Icon }) => (
              <button key={id} data-testid={`ao-order-type-${id}`} onClick={() => setOrderType(id)} className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-semibold ${orderType === id ? 'bg-sspice text-white border-sspice' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>

          {/* Customer (operational) */}
          <div className="grid grid-cols-2 gap-2 mb-1">
            <Input placeholder="Customer phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="ao-customer-phone" />
            <Input placeholder="Name (optional)" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="ao-customer-name" />
          </div>
          {customerLookup === 'searching' && (
            <p className="text-xs text-slate-400 mb-3" data-testid="ao-lookup-searching">Checking…</p>
          )}
          {customerLookup?.found === true && (
            <div className="mb-3">
              <p className="text-xs text-emerald-600 font-medium" data-testid="ao-lookup-found">
                ✓ Existing customer{customerLookup.name ? `: ${customerLookup.name}` : ''}
              </p>
              {customerLookup.address && (
                <p className="text-[11px] text-slate-500 mt-0.5" data-testid="ao-lookup-address">📍 {customerLookup.address}</p>
              )}
            </div>
          )}
          {customerLookup?.found === false && !showAddCustomer && (
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-slate-400" data-testid="ao-lookup-new">New customer</p>
              <button
                data-testid="ao-add-customer-btn"
                onClick={() => setShowAddCustomer(true)}
                className="text-xs font-semibold text-sspice"
              >
                + Add Customer
              </button>
            </div>
          )}
          {customerLookup?.found === false && showAddCustomer && (
            <div className="mb-3 space-y-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-[11px] text-slate-500">Mobile is already captured above. Name and address are optional.</p>
              <Input
                placeholder="Address (optional)"
                value={newCustomerAddress}
                onChange={(e) => setNewCustomerAddress(e.target.value)}
                data-testid="ao-new-customer-address"
              />
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-sspice hover:bg-sspice-hover" onClick={handleAddCustomer} disabled={addingCustomer} data-testid="ao-save-new-customer">
                  {addingCustomer ? 'Saving…' : 'Save Customer'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddCustomer(false)}>Cancel</Button>
              </div>
            </div>
          )}
          {!customerLookup && <div className="mb-3" />}

          {/* Post Pay + Tent Card */}
          <div className="flex items-center gap-2 mb-3">
            <button
              data-testid="ao-post-pay-toggle"
              onClick={() => setIsPostPay((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold flex-shrink-0 ${isPostPay ? 'bg-amber-500 text-white border-amber-500' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
            >
              <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${isPostPay ? 'border-white bg-white' : 'border-slate-400'}`}>
                {isPostPay && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
              </span>
              Post Pay
            </button>
            <Input
              placeholder="Tent card #"
              value={tentCardNumber}
              onChange={(e) => setTentCardNumber(e.target.value)}
              className="flex-1"
              data-testid="ao-tent-card"
            />
          </div>

          {/* Kitchen note — shows up prominently on the Kitchen Display
              System screen for this order, e.g. "extra spicy", "no onion",
              "customer allergic to nuts". Full-width since these tend to
              run longer than a tent card number. */}
          <div className="px-4 pb-2">
            <Input
              placeholder="Note for kitchen (optional) — e.g. extra spicy, no onions"
              value={kitchenNotes}
              onChange={(e) => setKitchenNotes(e.target.value)}
              className="w-full text-sm"
              data-testid="ao-kitchen-notes"
            />
          </div>

          {/* Lines */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[120px]">
            {cart.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10">Add items from the catalog.</p>
            ) : cart.map((c) => (
              <div key={c.signature} className="flex items-start gap-2 p-2 border border-slate-100 rounded-lg" data-testid={`ao-line-${c.signature}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{c.menu_item_name}</p>
                  {c.variant?.name && <p className="text-[11px] text-slate-500">{c.variant.name}</p>}
                  {(c.modifiers || []).length > 0 && <p className="text-[11px] text-slate-500">{c.modifiers.map((m) => m.name).join(', ')}</p>}
                  <p className="text-[11px] text-slate-400">{formatINR(engine.unitPrice(c))} each</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-1 bg-sspice rounded-full px-1 py-0.5">
                    <button onClick={() => setQty(c.signature, c.qty - 1)} className="w-5 h-5 flex items-center justify-center text-white"><Minus className="w-3 h-3" /></button>
                    <span className="text-white text-xs font-bold w-4 text-center">{c.qty}</span>
                    <button onClick={() => setQty(c.signature, c.qty + 1)} className="w-5 h-5 flex items-center justify-center text-white"><Plus className="w-3 h-3" /></button>
                  </div>
                  <span className="text-sm font-bold text-slate-800">{formatINR(engine.lineTotal(c))}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Totals + place */}
          <div className="border-t border-slate-200 pt-3 mt-2">
            <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{formatINR(totals.subtotal)}</span></div>
            {orderType === 'DELIVERY' && (
              <div className="flex justify-between text-sm mb-1" data-testid="ao-delivery-fee"><span className="text-slate-500">Delivery</span><span className="font-semibold">{quote ? (quote.delivery.free ? 'FREE' : `₹${quote.delivery_fee.toFixed(0)}`) : '—'}</span></div>
            )}
            {quote && quote.tax > 0 && (
              <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">GST{quote.tax_percent ? ` (${quote.tax_percent}%)` : ''}</span><span className="font-semibold">₹{quote.tax.toFixed(0)}</span></div>
            )}
            <div className="flex justify-between text-base font-bold mb-2"><span>Total</span><span data-testid="ao-total">₹{(quote ? quote.total : totals.subtotal / 100).toFixed(0)}</span></div>
            <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={placeOrder} disabled={placing || cart.length === 0} data-testid="ao-place-order">
              {placing ? 'Placing…' : paidOrder ? `Collect Payment · ₹${(quote ? quote.total : totals.subtotal / 100).toFixed(0)}` : `Place Order · ₹${(quote ? quote.total : totals.subtotal / 100).toFixed(0)}`}
            </Button>
            {paidOrder && (
              <p className="text-[11px] text-amber-600 text-center mt-1.5" data-testid="ao-pending-order-note">
                Order #{paidOrder.order_number} is saved, awaiting payment
              </p>
            )}
          </div>
        </aside>
      </div>

      {customizing && <ProductCustomizer product={customizing} onClose={() => setCustomizing(null)} onConfirm={confirmCustomize} />}
      <PaymentDialog
        order={paidOrder}
        open={showPayment}
        onClose={() => {
          // Cancelling here only closes the popup — the order stays
          // exactly as it was, cart untouched. The cashier can retry via
          // "Collect Payment" above, or explicitly hit "Clear" to abandon
          // it. Nothing here silently empties the cart on their behalf.
          setShowPayment(false);
        }}
        onPaid={() => {
          setShowPayment(false);
          setPaidOrder(null);
          clear();
          setCustomer({ name: '', phone: '' });
          setTentCardNumber('');
          setKitchenNotes('');
          setIsPostPay(false);
        }}
      />

      {/* Held Orders — parked drafts, resumable at any time */}
      <Dialog open={showHeldOrders} onOpenChange={setShowHeldOrders}>
        <DialogContent className="max-w-md" data-testid="ao-held-orders-dialog">
          <DialogHeader>
            <DialogTitle>Held Orders ({heldOrders.length})</DialogTitle>
          </DialogHeader>
          {heldOrders.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No orders on hold right now.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {heldOrders.map((held) => {
                const itemCount = held.cart.reduce((s, c) => s + c.qty, 0);
                const heldTotal = engine.computeTotals(held.cart)?.total ?? 0;
                const heldAgo = Math.max(0, Math.round((Date.now() - new Date(held.heldAt).getTime()) / 60000));
                return (
                  <div key={held.id} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {held.customer?.name || held.customer?.phone || 'Walk-in'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {itemCount} item{itemCount !== 1 ? 's' : ''} · {formatINR(heldTotal)} · {held.orderType} · held {heldAgo}m ago
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button size="sm" onClick={() => handleResumeHeldOrder(held)} data-testid="ao-resume-held-btn">
                        Resume
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => handleDeleteHeldOrder(held.id)}
                        data-testid="ao-delete-held-btn"
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
